import { Box } from "@mui/material";
import { type FC, useCallback, useEffect, useMemo } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { SkeletonWithRandomWidths } from "../../../pages/Profile/v2/util/constants";
import { NotificationType, type NurseFullRole, type ReplaceShiftRequest, type ShiftRequest } from "../../../utils/models";
import { getShiftIndexByShiftTimes } from "../../../utils/utilFunctions";
import { AddNurse } from "../../AddNurse/AddNurse";
import { usePickOpenShift } from "../../OpenShiftCard/hooks";
import { DeferMount } from "../../UIToolkit/DeferMount/DeferMount";
import { MultiSizePopup } from "../../UIToolkit/Popups/MultiSizePopup/MultiSizePopup";
import { MultiSizePopupState } from "../../UIToolkit/Popups/MultiSizePopup/utils";
import { useRequestUtils } from "../Provider/useRequestUtils";
import { useManualAddCompletion } from "./useManualAddCompletion";

type Props = {
	open: boolean;
	onClose: () => void;
	approvedNursesIds: number[];
	request: ShiftRequest;
};

export const MinimizableManualAddStaff: FC<Props> = ({ open, onClose, request, approvedNursesIds }) => {
	const { track } = useAnalytics();
	const { getDayIndexByDateTime, chosenDepartment } = useHospitalData();
	const { completeManualAdd, isCompleting, isCompletionSuccess } = useManualAddCompletion();
	const { mutateAsync: pickOpenShift } = usePickOpenShift();
	const { approveRequest } = useRequestUtils();
	const isReplaceRequest = request.request_type === NotificationType.ReplaceShift;

	const handleClose = useCallback(() => {
		if (isCompleting) return; // Prevent closing while deleting or updating
		onClose();
	}, [onClose, isCompleting]);

	const handleAddCompletedForReplace = useCallback(
		async (nurseId: number) => {
			await pickOpenShift({
				openShiftId: request.id,
				nurseId: nurseId,
				selectedStart: request.message.shift_start,
				selectedEnd: request.message.shift_end,
				departmentId: chosenDepartment.department_id,
			});
			await approveRequest(request, nurseId);
			track("ManualStaffReplace_Completed", {
				source: "MinimizableManualAddStaff",
				requestId: request.id,
			});
		},
		[pickOpenShift, request, approveRequest, track, chosenDepartment],
	);

	const handleAddCompletedForOpenShift = useCallback(async () => {
		await completeManualAdd({
			request,
			approvedNursesCount: approvedNursesIds.length,
			source: "MinimizableManualAddStaff",
		});
	}, [request, approvedNursesIds, completeManualAdd]);

	const handleAddCompleted = useCallback(
		async (didAddStaffOrNurseId: boolean | number) => {
			if (typeof didAddStaffOrNurseId === "number" && isReplaceRequest) {
				await handleAddCompletedForReplace(didAddStaffOrNurseId);
			} else if (didAddStaffOrNurseId) {
				await handleAddCompletedForOpenShift();
			}
		},
		[handleAddCompletedForReplace, isReplaceRequest, handleAddCompletedForOpenShift],
	);

	useEffect(() => {
		if (isCompletionSuccess) {
			handleClose();
		}
	}, [isCompletionSuccess, handleClose]);

	const nurseIdsToIgnore = useMemo(() => (isReplaceRequest ? [(request as ReplaceShiftRequest).replacement_nurse_id] : undefined), [isReplaceRequest, request]);

	return (
		<MultiSizePopup
			title={"Manual Staff Addition"}
			open={open}
			onClose={handleClose}
			disableMinimize={true}
			bodyRenderer={(currentSizeState) => {
				switch (currentSizeState) {
					case MultiSizePopupState.MAXIMIZED:
						return (
							<Box
								sx={{
									maxWidth: "1200px",
								}}
							>
								<DeferMount
									fallback={
										<Box
											sx={{
												minWidth: "800px",
											}}
										>
											<SkeletonWithRandomWidths randomLines={true} lines={10} header={true} />
										</Box>
									}
								>
									<AddNurse
										setOpen={(_) => {}}
										setIsAdd={handleAddCompleted}
										onNurseAddedAvoidActualAdding={isReplaceRequest ? handleAddCompleted : undefined}
										dayNo={getDayIndexByDateTime(request.message.date) ?? 0}
										shiftNo={getShiftIndexByShiftTimes({
											startTime: request.message.shift_start,
											endTime: request.message.shift_end,
										})}
										onClose={handleClose}
										rolesToDisplay={request.message.roles as NurseFullRole[]}
										lockedTimes={{
											startTime: request.message.shift_start,
											endTime: request.message.shift_end,
										}}
										nurseIdsToIgnore={nurseIdsToIgnore}
										requestJobId={request.job_id}
										skipRedundantOpenShiftSuggestion={true}
										// A request is filled by exactly one nurse.
										selectionMode="single"
									/>
								</DeferMount>
							</Box>
						);
				}
				return null;
			}}
		/>
	);
};
